import { Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { ApiHttpClient } from 'app/http/clients/api';
import { environment } from 'environments/environment';
import { Borrower, PartnerRestrictedStatus } from 'app/models/dto/borrower.model';
import { ERCInterest } from 'app/modules/borrower/models/erc-interest/erc-interest.model';
import { BorrowerOwnerHistory } from 'ng2-app/models/borrower/BorrowerOwnerHistory.model';

export interface Params {
  [param: string]: string | string[];
}

@Injectable({
  providedIn: 'root'
})
export class BorrowerService {

  constructor(private http: ApiHttpClient) { }

  search(search: string): Observable<Borrower[]> {
    return this.http.get<Borrower[]>(`${environment.API_URL}/borrower-search`, {
      params: {
        search,
        query: encodeURIComponent(JSON.stringify({
          criteria: {},
          sort: { 'borrowers.viewed': 'DESC' },
          limit: 10,
          offset: 0
        }))
      }
    });
  }

  get(borrowerId: number): Observable<Borrower> {
    return this.http.get<Borrower>(`${environment.API_URL}/borrower/${borrowerId}`);
  }

  update(borrowerId: number, updates: any): Observable<Borrower> {
    return this.http.put<Borrower>(`${environment.API_URL}/borrower/${borrowerId}`, updates);
  }

  assignEmbeddedBorrower(borrowerId: number) {
    return this.http.put(`${environment.API_URL}/borrower/${borrowerId}/embedded/reassign-borrower`, {});
  }

  getBorrowerProfile(borrowerId: number): Observable<[]> {
    return this.http.get<[]>(`${environment.API_URL}/borrower-profile/${borrowerId}`);
  }

  getTradeLines(borrowerId: number): Observable<[]> {
    return this.http.get<[]>(`${environment.API_URL}/borrower/${borrowerId}/tradelines`);
  }

  updateProfile(borrowerId: number, attrs: any) {
    return this.http.put(`${environment.API_URL}/borrower-profile/${borrowerId}`, attrs || {});
  }

  setDateValueAttributes(borrowerId: number, attributeId: number) {
    return this.http.put(`${environment.API_URL}/borrower-profile/${borrowerId}/validate-date-value-attributes/${attributeId}`, {});
  }

  assignToUserId(borrowerId: number, userId: number) {
    return this.http.put(`${environment.API_URL}/borrower/${borrowerId}`, { id: borrowerId, assignedLenderId: userId });
  }

  getOwnershipHistory(borrowerId: number) {
    return this.http.get<BorrowerOwnerHistory>(`${environment.API_URL}/borrower/${borrowerId}/marketing/ownership-history`);
  }

  getPartnerRestrictedStatus(borrowerId: number): Observable<PartnerRestrictedStatus> {
    return this.http.get<PartnerRestrictedStatus>(`${environment.API_URL}/borrower/${borrowerId}/partner-restricted`);
  }

  shareBorrower(borrowerIds: number[], userIdsToShare: number[]) {
    return this.http.post(`${environment.API_URL}/share-borrower`, {
      borrowers: borrowerIds,
      sharedWith: userIdsToShare,
    });
  }

  unshareBorrower(borrowerIds: number[], userIdsToUnshare: number[], removeFromQueue: boolean | null = null) {
    const params = {
      borrowers: JSON.stringify(borrowerIds),
      sharedWith: JSON.stringify(userIdsToUnshare)
    } as Params;

    if (removeFromQueue !== null) {
      params.removeFromQueue = removeFromQueue.toString();
    }
    return this.http.delete(`${environment.API_URL}/share-borrower`, { params });
  }

  removeBorrowerFromApprovalsExperiment(userId) {
    return this.http.delete(`${environment.API_URL}/user/${userId}/approvalsExperiment`);
  }

  getERCInterest(borrowerId: number): Observable<ERCInterest> {
    return this.http.get<ERCInterest>(`${environment.API_URL}/borrower/${borrowerId}/erc-interest`)
      .pipe(
        catchError(() => of())
      );
  }

  getRenewalEligibilityDate(borrowerId: number): Observable<{ estimatedRenewalEligibility: string }> {
    return this.http.get<{ estimatedRenewalEligibility: string }>(`${environment.API_URL}/borrower/${borrowerId}/renewal-eligibility-date`);
  }
}
